// Access (/internal/access; VRG-727 — "People" at /internal/people until 2026-10-20) — ONE
// table, one row per person, replacing the Signups page's Waitlist / Allowlist / Site invites
// tabs (Spencer, 2026-10-01). "People" now names GTM → Sales' tab of people on the pipeline.
//
// It is a VIEW: rows are built by the API from the waitlist (signups), the
// platform allowlist, pricing access and site-gate invites, matched on
// normalised email (apps/api/src/features/people-access/people-view.ts, which
// also documents how each system's states map onto the shared pills). Nothing
// was merged or migrated; every action below calls the mechanism that already
// owned it. The old page stays reachable at /internal/signups/classic for the
// actions this one does not carry (comp subscription, grant activations,
// reject, the forwarded website/ROI fields).
//
// ⚠️ NO AUTOMATIC EMAIL. Grants are silent; the links come back to be copied.
// Pricing and platform grants carry an explicit, unticked "Send … email" box.
import React, { useMemo, useState } from 'react'
import { Link as RouterLink, Navigate } from 'react-router-dom'
import { Box, Stack, Typography } from '@mui/material'
import {
  BanIcon,
  CalendarClockIcon,
  CopyIcon,
  GlobeIcon,
  KeyRoundIcon,
  LayoutDashboardIcon,
  LinkIcon,
  MousePointerClickIcon,
  RadioTowerIcon,
  RefreshCwIcon,
  StickyNoteIcon,
  TagIcon,
  UserPlusIcon,
} from 'lucide-react'
import { Button, EMPTY_VALUE } from '@repo/ui'

import { ROUTES, CLASSIC_ACCESS_TITLE } from 'routes'
import { ActionDropdown, ActivityTimeCell, ConfirmSettingModal, EmptyState, Header, SourceChannelModal, StatusChip, Table } from 'components'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import type { ActionDropdownItem } from 'components/input/ActionDropdown/ActionDropdown'
import { PEOPLE_ACCESS_STATUSES } from 'constants/statuses'
// The Demos page's own status map, so the Demo column reads exactly as that page does.
import { DEMO_ENGAGEMENT_STATUSES } from 'pages/internal/InternalDemosPage/demoStatuses'
import { sourceChannelLabel } from 'constants/sourceChannels'
import { useSnackbar } from 'contexts'
import { useCanAccess, useLinkedSearch } from 'hooks'
import { date, getErrorMessage } from 'utils'
import {
  matchesPeopleFilter,
  usePeople,
  usePeopleActions,
  type AccessStatus,
  type GrantResult,
  type PeopleFilter,
  type Person,
} from 'hooks/api/usePeople'
import { useSetPersonSourceChannel, useSourceChannels } from 'hooks/api/useGtmSales'
import { useTrackedLinks } from 'hooks/api/useTrackedLinks'
import type { SourceChannel } from '@repo/types'
import type { GridFilterItem } from '@mui/x-data-grid-pro'

import { ExpiryDialog, GrantDialog, LinksModal, NoteDialog } from './dialogs'
// VRG-658 — the "Referred by" cell, shared with the classic page. The Referrals TAB that used to
// sit here moved to GTM → Affiliates → Refer & earn (VRG-684); `?tab=referrals` redirects there
// (App.tsx). The column stays: who referred a person is a fact about the person.
import { ReferredByBlock } from '../SignupsPage/referrals/StaffReferralsTable'
import { referredByOf } from '../SignupsPage/referrals/referralLabels'
// Newsletter / Country / Privacy notice — the classic page's columns, fed from the API's
// `waitlist_consent` (only those keys) instead of the whole waitlist blob.
import { buildConsentColumns } from '../SignupsPage/consentColumns'
import { buildFirstTouchColumn } from '../SignupsPage/firstTouchColumn'
import { InvitePersonModal } from './InvitePersonModal'
// Invite links (2026-10-09): vera.inc/invite/<code> made for a person; shared with Analytics → Website.
import { CreateTrackedLinkModal, PersonTrackedLinksModal } from '../TrackedLinks'
import { summariseByPerson, type PersonLinkSummary } from '../TrackedLinks/personSummary'

const cellFlexSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

/**
 * The VIEW facet — Requests / Invited / Active / Revoked — lives in the table's own Filter
 * drawer, not in a pill row above it (Spencer, 2026-10-01). It rides on a HIDDEN column of
 * `type: 'statusChip'`, the same mechanism every status filter in this app uses, so the
 * drawer renders it as the standard pills, the Filter button's applied-count badge counts
 * it, and a saved view (`viewKey`) can hold "Requests" as a one-click view.
 *
 * Its value is a LIST: a row can be Invited on one column and Active on another, so
 * `isAnyOf` here means "the row is in any of the chosen views". The matching itself is
 * `matchesPeopleFilter`, which mirrors the API's tested `matchesFilter`.
 */
const VIEW_STATUSES = {
  requests: { ...PEOPLE_ACCESS_STATUSES.requested, label: 'Requests' },
  invited: { ...PEOPLE_ACCESS_STATUSES.invited, label: 'Invited' },
  active: { ...PEOPLE_ACCESS_STATUSES.granted, label: 'Active' },
  revoked: { ...PEOPLE_ACCESS_STATUSES.revoked, label: 'Revoked' },
}
const VIEW_FIELD = 'view'

const viewIsAnyOf = [
  {
    value: 'isAnyOf',
    label: 'is any of',
    getApplyFilterFn: (item: GridFilterItem) => {
      const wanted = (Array.isArray(item.value) ? item.value : item.value ? [item.value] : []) as PeopleFilter[]
      if (!wanted.length) return null
      return (_value: unknown, row: Person) => wanted.some((f) => matchesPeopleFilter(row, f))
    },
  },
]

/** The three access columns are filterable the same way, on their own status. */
const ACCESS_OPTIONS = statusFilterOptions(PEOPLE_ACCESS_STATUSES)

const AccessCell = ({ status, detail }: { status: AccessStatus; detail?: string | null }) => (
  <Stack justifyContent="center" spacing={0.5} sx={{ height: '100%', minWidth: 0 }}>
    {status === 'none' ? (
      <Typography variant="body2" color="text.disabled">
        {EMPTY_VALUE}
      </Typography>
    ) : (
      <Box>
        <StatusChip size="small" statuses={PEOPLE_ACCESS_STATUSES} status={status} />
      </Box>
    )}
    {detail && (
      <Typography variant="body3" color="text.secondary" noWrap>
        {detail}
      </Typography>
    )}
  </Stack>
)

/**
 * VRG-675 — a People row carries its recorded source channel (GTM → Sales reads the same record).
 * Invite links add `links`: every live tracked link made for this People key, rolled up
 * (clicks from vera-2; last visit and top interest also from PostHog, see useTrackedLinks).
 */
type PersonRow = Person & { id: string; source_channel: SourceChannel | null; links: PersonLinkSummary | null }

const displayName = (p: Person) => p.name || p.email || 'Unnamed'

type Dialog =
  | { kind: 'grant-pricing' | 'grant-platform'; person: Person }
  | { kind: 'create-website' | 'edit-expiry'; person: Person }
  | { kind: 'revoke-website' | 'revoke-pricing' | 'revoke-platform'; person: Person }
  | { kind: 'regenerate-website'; person: Person }
  | { kind: 'note'; person: Person }
  | { kind: 'source-channel'; person: Person }
  | { kind: 'tracked-links'; person: Person }
  | null

export const InternalPeoplePage = () => {
  const { open: notification } = useSnackbar()
  // platform_admin only, as the Signups page was (InternalRoute also admits
  // platform_manager, so the page keeps its own guard).
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  // VRG-712 — opened from a lead journey on one person: their email seeds the search.
  const linkedSearch = useLinkedSearch()
  const { data: people = [], isLoading } = usePeople()
  const actions = usePeopleActions()
  const { data: channels } = useSourceChannels(isAdmin)
  const setChannel = useSetPersonSourceChannel()
  const { data: tracked } = useTrackedLinks(isAdmin)
  const linksByPerson = useMemo(() => summariseByPerson(tracked?.links ?? []), [tracked])
  // `undefined` = closed; `null` = open from the header (no person chosen yet).
  const [linkFor, setLinkFor] = useState<Person | null | undefined>(undefined)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [dialog, setDialog] = useState<Dialog>(null)
  const [shown, setShown] = useState<{ result: GrantResult; name: string } | null>(null)

  const rows: PersonRow[] = useMemo(
    () =>
      people.map((p) => ({
        ...p,
        id: p.key,
        source_channel: channels?.people[p.key] ?? null,
        links: linksByPerson.get(p.key) ?? null,
      })),
    [people, channels, linksByPerson],
  )

  const fail = (error: Error) => notification({ type: 'error', title: getErrorMessage(error) })
  const close = () => setDialog(null)
  const showLinks = (person: Person) => (result: GrantResult) => {
    close()
    setShown({ result, name: displayName(person) })
  }

  const copyPlatformLink = (person: Person) =>
    actions.platformLink.mutate(
      { key: person.key },
      {
        onSuccess: ({ platform_url }) => {
          void navigator.clipboard.writeText(platform_url)
          notification({ type: 'success', title: 'Platform signup link copied' })
        },
        onError: fail,
      },
    )

  const menu = (p: Person): ActionDropdownItem[] => {
    const hasEmail = Boolean(p.email)
    const w = p.website.status
    const websiteLive = w === 'invited' || w === 'granted'
    const noEmailTip = hasEmail ? undefined : 'Needs an email address'
    return [
      // ── Website
      websiteLive
        ? { label: 'Regenerate website link', icon: <RefreshCwIcon strokeWidth={1} />, onClick: () => setDialog({ kind: 'regenerate-website', person: p }) }
        : { label: 'Create website link', icon: <GlobeIcon strokeWidth={1} />, onClick: () => setDialog({ kind: 'create-website', person: p }) },
      {
        label: 'Copy website link',
        icon: <CopyIcon strokeWidth={1} />,
        disabled: true,
        tooltip: 'Website links are shown once and only a fingerprint is stored. Regenerate to get a new one.',
        onClick: () => undefined,
      },
      {
        label: 'Edit website expiry',
        icon: <CalendarClockIcon strokeWidth={1} />,
        disabled: !websiteLive,
        tooltip: websiteLive ? undefined : 'No live website link',
        onClick: () => setDialog({ kind: 'edit-expiry', person: p }),
      },
      // ── Pricing
      p.pricing.status === 'granted' || p.pricing.status === 'invited'
        ? {
            label: 'Revoke pricing access',
            icon: <BanIcon strokeWidth={1} />,
            divider: true,
            disabled: p.platform.status === 'granted' || p.platform.status === 'invited',
            tooltip:
              p.platform.status === 'granted' || p.platform.status === 'invited'
                ? 'Platform access includes pricing. Revoke platform access first.'
                : undefined,
            onClick: () => setDialog({ kind: 'revoke-pricing', person: p }),
          }
        : {
            label: p.pricing.status === 'expired' ? 'Send a new pricing link' : 'Grant pricing access',
            icon: <TagIcon strokeWidth={1} />,
            divider: true,
            disabled: !hasEmail,
            tooltip: noEmailTip,
            onClick: () => setDialog({ kind: 'grant-pricing', person: p }),
          },
      // ── Platform
      p.platform.status === 'invited' || p.platform.status === 'granted'
        ? { label: 'Copy platform signup link', icon: <CopyIcon strokeWidth={1} />, disabled: p.platform.has_account, tooltip: p.platform.has_account ? 'Already has an account' : undefined, onClick: () => copyPlatformLink(p) }
        : {
            label: 'Grant platform access',
            icon: <LayoutDashboardIcon strokeWidth={1} />,
            disabled: !hasEmail,
            tooltip: noEmailTip,
            onClick: () => setDialog({ kind: 'grant-platform', person: p }),
          },
      // ── Note
      { label: p.note ? 'Edit note' : 'Add note', icon: <StickyNoteIcon strokeWidth={1} />, divider: true, onClick: () => setDialog({ kind: 'note', person: p }) },
      // VRG-675 — where they came from; feeds GTM → Sales.
      { label: 'Set source channel', icon: <RadioTowerIcon strokeWidth={1} />, onClick: () => setDialog({ kind: 'source-channel', person: p }) },
      // ── Invite links
      { label: 'Create tracked link', icon: <LinkIcon strokeWidth={1} />, divider: true, onClick: () => setLinkFor(p) },
      {
        label: 'Tracked links',
        icon: <MousePointerClickIcon strokeWidth={1} />,
        disabled: !linksByPerson.get(p.key),
        tooltip: linksByPerson.get(p.key) ? undefined : 'No tracked links yet',
        onClick: () => setDialog({ kind: 'tracked-links', person: p }),
      },
      // ── Destructive, last (§73)
      {
        label: 'Revoke website access',
        icon: <BanIcon strokeWidth={1} />,
        destructive: true,
        divider: true,
        disabled: !websiteLive,
        tooltip: websiteLive ? undefined : 'No live website link',
        onClick: () => setDialog({ kind: 'revoke-website', person: p }),
      },
      {
        label: 'Revoke platform access',
        icon: <BanIcon strokeWidth={1} />,
        destructive: true,
        disabled: p.platform.status !== 'invited',
        tooltip:
          p.platform.has_account
            ? 'Already has an account. Manage it from Companies.'
            : p.platform.status !== 'invited'
              ? 'Not on the platform allowlist'
              : undefined,
        onClick: () => setDialog({ kind: 'revoke-platform', person: p }),
      },
    ]
  }

  const columns = useMemo(
    () => [
      {
        field: VIEW_FIELD,
        headerName: 'Show',
        type: 'statusChip',
        options: statusFilterOptions(VIEW_STATUSES),
        filterOperators: viewIsAnyOf,
        sortable: false,
        disableColumnMenu: true,
        hideable: false,
        width: 0,
        valueGetter: (_v: unknown, row: Person) =>
          (Object.keys(VIEW_STATUSES) as PeopleFilter[]).filter((f) => matchesPeopleFilter(row, f)).join(', '),
      },
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        width: 260,
        minWidth: 220,
        flex: 260,
        sortable: true,
        disableColumnMenu: true,
        pinned: 'left',
        valueGetter: (_v: unknown, row: Person) => `${row.name ?? ''} ${row.email ?? ''}`,
        renderCell: ({ row }: { row: Person }) => (
          <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
            <Typography variant="body2" noWrap title={row.note ?? undefined}>
              {displayName(row)}
              {row.note ? (
                <StickyNoteIcon strokeWidth={1} size={13} style={{ marginLeft: 6, verticalAlign: '-1px', opacity: 0.6 }} />
              ) : null}
            </Typography>
            <Typography variant="body3" color="text.secondary" noWrap>
              {row.email ?? 'Link only'}
            </Typography>
          </Stack>
        ),
      },
      {
        // The PLATFORM company (their account's, linked) wins over the name they typed on
        // a form; the typed name is shown under it only when it says something different.
        field: 'company',
        headerName: 'Company',
        type: 'string',
        width: 200,
        minWidth: 160,
        flex: 200,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: Person) => row.platform_company?.name ?? row.company ?? '',
        renderCell: ({ row }: { row: Person }) => {
          const typed = row.company && row.company !== row.platform_company?.name ? row.company : null
          return (
            <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
              {row.platform_company ? (
                <Typography
                  component={RouterLink}
                  to={ROUTES.INTERNAL_COMPANY(row.platform_company.id)}
                  variant="body2"
                  noWrap
                  title={row.platform_company.name}
                  sx={{ color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {row.platform_company.name}
                </Typography>
              ) : (
                <Typography variant="body2" color={row.company ? 'text.secondary' : 'text.disabled'} noWrap title={row.company ?? undefined}>
                  {row.company ?? EMPTY_VALUE}
                </Typography>
              )}
              {row.platform_company && typed && (
                <Typography variant="body3" color="text.secondary" noWrap title={`Typed on the form: ${typed}`}>
                  {typed}
                </Typography>
              )}
            </Stack>
          )
        },
      },
      {
        field: 'sources',
        headerName: 'Source',
        type: 'string',
        // Measured: "Request access +1" (a request that also sent an ROI model) needs 128px
        // of text; 160 left 112 and ellipsized it.
        width: 180,
        minWidth: 160,
        flex: 180,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: Person) => row.sources.join(', '),
        renderCell: ({ row }: { row: Person }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color="text.secondary" noWrap title={row.sources.join(', ')}>
              {row.sources[0] ?? EMPTY_VALUE}
              {row.sources.length > 1 ? ` +${row.sources.length - 1}` : ''}
            </Typography>
          </Box>
        ),
      },
      // VRG-728 — where their website visit came from; same column as the classic grid.
      buildFirstTouchColumn(),
      /*
        VRG-658 — "Referred by": a brand (company, then the member who sent it), an Insider
        (the FirstPromoter ref), or empty. Referred REQUESTS are sorted to the top by the API;
        none of them skip the queue.
      */
      {
        field: 'referral',
        headerName: 'Referred by',
        type: 'string',
        width: 200,
        minWidth: 160,
        flex: 200,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: Person) => referredByOf(row.referral)?.primary ?? '',
        renderCell: ({ row }: { row: Person }) =>
          row.referral ? (
            <ReferredByBlock referral={row.referral} />
          ) : (
            <Box sx={cellFlexSx}>
              <Typography variant="body2" color="text.disabled">
                {EMPTY_VALUE}
              </Typography>
            </Box>
          ),
      },
      /*
        VRG-675 — the Traction channel that brought this person (Outreach for an email or a
        field visit). Not the "Source" column to its left, which says which SYSTEM the row is
        from (waitlist, allowlist, invite). Filterable; unset reads "Not recorded".
      */
      {
        field: 'source_channel',
        headerName: 'Source channel',
        type: 'multiSelect',
        width: 190,
        minWidth: 170,
        flex: 190,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: PersonRow) => sourceChannelLabel(row.source_channel),
        // The filter drawer calls this with `{ filter: true, row: { source_channel: <label> } }`.
        renderCell: ({ row, filter }: { row: PersonRow; filter?: boolean }) => filter ? sourceChannelLabel(row.source_channel) : (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color={row.source_channel ? 'text.primary' : 'text.disabled'} noWrap>
              {row.source_channel ? sourceChannelLabel(row.source_channel) : EMPTY_VALUE}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'website',
        headerName: 'Website',
        type: 'statusChip',
        options: ACCESS_OPTIONS,
        width: 150,
        minWidth: 140,
        flex: 150,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: Person) => row.website.status,
        renderCell: ({ row }: { row: Person }) => (
          <AccessCell
            status={row.website.status}
            detail={
              row.website.expires_at && (row.website.status === 'invited' || row.website.status === 'granted')
                ? `until ${date(row.website.expires_at, 'MMM dd')}`
                : null
            }
          />
        ),
      },
      {
        field: 'pricing',
        headerName: 'Pricing',
        type: 'statusChip',
        options: ACCESS_OPTIONS,
        width: 130,
        minWidth: 120,
        flex: 130,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: Person) => row.pricing.status,
        renderCell: ({ row }: { row: Person }) => <AccessCell status={row.pricing.status} />,
      },
      {
        field: 'platform',
        headerName: 'Platform',
        type: 'statusChip',
        options: ACCESS_OPTIONS,
        width: 130,
        minWidth: 120,
        flex: 130,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: Person) => row.platform.status,
        renderCell: ({ row }: { row: Person }) => (
          <AccessCell status={row.platform.status} detail={row.platform.has_account ? 'Has account' : null} />
        ),
      },
      {
        // Demos are tracked PER COMPANY. Matched by the demo's prospect contact email,
        // else the person's account being a member of the demo company, else their email
        // domain equalling the demo's prospect domain (never for consumer mail domains).
        // Linked to the demo company — the same destination the Demos page links to.
        field: 'demo',
        headerName: 'Demo',
        type: 'statusChip',
        options: statusFilterOptions(DEMO_ENGAGEMENT_STATUSES),
        width: 150,
        minWidth: 140,
        flex: 150,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: Person) => row.demo?.status ?? '',
        renderCell: ({ row }: { row: Person }) => {
          if (!row.demo) {
            return (
              <Box sx={cellFlexSx}>
                <Typography variant="body2" color="text.disabled">
                  {EMPTY_VALUE}
                </Typography>
              </Box>
            )
          }
          const how =
            row.demo.match === 'contact'
              ? 'their email is the demo contact'
              : row.demo.match === 'member'
                ? 'their account is in the demo company'
                : 'company demo, matched on their email domain'
          const chip = <StatusChip size="small" statuses={DEMO_ENGAGEMENT_STATUSES} status={row.demo.status} />
          return (
            <Stack justifyContent="center" spacing={0.5} sx={{ height: '100%', minWidth: 0 }} title={`${row.demo.company_name}: ${how}`}>
              <Box>
                {row.demo.status === 'purged' ? (
                  chip
                ) : (
                  <RouterLink to={ROUTES.INTERNAL_COMPANY(row.demo.company_id)} style={{ textDecoration: 'none' }}>
                    {chip}
                  </RouterLink>
                )}
              </Box>
              <Typography variant="body3" color="text.secondary" noWrap>
                {row.demo.company_name}
              </Typography>
            </Stack>
          )
        },
      },
      {
        field: 'last_seen_at',
        headerName: 'Last seen',
        type: 'dateTime',
        width: 170,
        minWidth: 160,
        flex: 170,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: Person) => (row.last_seen_at ? new Date(row.last_seen_at) : null),
        // VRG-705 — date + time, how long ago, full stamp on hover (the shared last-activity cell).
        renderCell: ({ row }: { row: Person }) => <ActivityTimeCell at={row.last_seen_at} color="text.secondary" />,
      },
      /*
        Invite links. "Link clicks" counts human clicks on every live link made for this
        person (vera-2, real everywhere; bots left out). "Last visit" is the later of their
        last click and their last page view with analytics on; "Top interest" is the section of
        vera.inc they looked at most after clicking (PostHog, visitors who allow analytics).
        Anyone they forward a link to counts as them.
      */
      {
        field: 'link_clicks',
        headerName: 'Link clicks',
        type: 'number',
        // "Link clicks" plus the sort arrow needs ~130px; 120 ellipsized it.
        width: 140,
        minWidth: 136,
        flex: 140,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: PersonRow) => (row.links ? row.links.clicks : null),
        renderCell: ({ row }: { row: PersonRow }) =>
          row.links ? (
            <Box sx={cellFlexSx}>
              <Typography
                component="button"
                type="button"
                variant="body2"
                onClick={() => setDialog({ kind: 'tracked-links', person: row })}
                title={`${row.links.links.length} tracked link${row.links.links.length === 1 ? '' : 's'}`}
                sx={{ p: 0, border: 0, bgcolor: 'transparent', color: 'text.primary', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
              >
                {row.links.clicks}
              </Typography>
            </Box>
          ) : (
            <Box sx={cellFlexSx}>
              <Typography variant="body2" color="text.disabled">
                {EMPTY_VALUE}
              </Typography>
            </Box>
          ),
      },
      {
        field: 'last_visit',
        headerName: 'Last visit',
        type: 'dateTime',
        width: 170,
        minWidth: 160,
        flex: 170,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: PersonRow) => (row.links?.last_visit_at ? new Date(row.links.last_visit_at) : null),
        renderCell: ({ row }: { row: PersonRow }) => <ActivityTimeCell at={row.links?.last_visit_at} color="text.secondary" />,
      },
      {
        field: 'top_interest',
        headerName: 'Top interest',
        type: 'string',
        width: 170,
        minWidth: 150,
        flex: 170,
        sortable: true,
        disableColumnMenu: true,
        valueGetter: (_v: unknown, row: PersonRow) => row.links?.top_interest ?? '',
        renderCell: ({ row }: { row: PersonRow }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color={row.links?.top_interest ? 'text.secondary' : 'text.disabled'} noWrap>
              {row.links?.top_interest ?? EMPTY_VALUE}
            </Typography>
          </Box>
        ),
      },
      ...buildConsentColumns((row: Person) => row.waitlist_consent),
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        disableReorder: true,
        pinned: 'right',
        renderCell: ({ row }: { row: Person }) => <ActionDropdown items={menu(row)} />,
      },
    ],
    // `menu` closes over setters only; rebuilding on `people` keeps tooltips current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [people, channels, linksByPerson],
  )

  if (!isAdmin) return <Navigate to={ROUTES.INTERNAL} replace />

  const person = dialog?.person
  const name = person ? displayName(person) : ''

  return (
    <>
      <Header
        title="Access"
        titleAdornment={
          <Typography
            component={RouterLink}
            to={ROUTES.INTERNAL_SIGNUPS_CLASSIC}
            variant="body3"
            color="text.secondary"
            // Beside the "Access" title this reads as the page's name; the full name is
            // what screen readers and the hover hear (CLASSIC_ACCESS_TITLE).
            aria-label={CLASSIC_ACCESS_TITLE}
            title={CLASSIC_ACCESS_TITLE}
            sx={{ ml: 2, textDecoration: 'underline', textUnderlineOffset: 3 }}
          >
            Classic view
          </Typography>
        }
        rightComponents={
          <Stack direction="row" spacing={2}>
            <Button variant="secondary" onClick={() => setLinkFor(null)} startIcon={<LinkIcon strokeWidth={1} />}>
              Create tracked link
            </Button>
            <Button variant="primary" disableElevation onClick={() => setInviteOpen(true)} startIcon={<UserPlusIcon strokeWidth={1} />}>
              Invite
            </Button>
          </Stack>
        }
      />
      <Stack spacing={4}>
        <Table
          loading={isLoading}
          columns={columns}
          rows={rows}
          alwaysShowRowSelection={false}
          fillBottom
          paginationMode="client"
          initialPageSize={100}
          // Saved views: staff can keep "Requests" (Filter → Show → Requests) as a
          // one-click view. The `view` facet column stays hidden; it exists to filter.
          viewKey="internal-people"
          search={linkedSearch}
          visibilityModel={{ [VIEW_FIELD]: false }}
          emptyState={
            <EmptyState
              title="Nobody here yet"
              text="Requests from the website and everyone you invite appear here."
              icon={<KeyRoundIcon strokeWidth={1} size={32} />}
            />
          }
        />
      </Stack>

      <InvitePersonModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onDone={(result, who) => {
          setInviteOpen(false)
          setShown({ result, name: who })
        }}
      />

      <LinksModal result={shown?.result ?? null} name={shown?.name ?? ''} onClose={() => setShown(null)} />

      <CreateTrackedLinkModal open={linkFor !== undefined} person={linkFor ?? null} onClose={() => setLinkFor(undefined)} />

      {person && (
        <>
          <GrantDialog
            kind="pricing"
            name={name}
            open={dialog?.kind === 'grant-pricing'}
            loading={actions.grantPricing.isPending}
            onClose={close}
            onConfirm={(send) =>
              actions.grantPricing.mutate({ key: person.key, name: person.name ?? undefined, send_email: send }, { onSuccess: showLinks(person), onError: fail })
            }
          />
          <GrantDialog
            kind="platform"
            name={name}
            open={dialog?.kind === 'grant-platform'}
            loading={actions.grantPlatform.isPending}
            onClose={close}
            onConfirm={(send) =>
              actions.grantPlatform.mutate({ key: person.key, name: person.name ?? undefined, send_email: send }, { onSuccess: showLinks(person), onError: fail })
            }
          />
          <ExpiryDialog
            mode={dialog?.kind === 'edit-expiry' ? 'edit' : 'create'}
            name={name}
            open={dialog?.kind === 'create-website' || dialog?.kind === 'edit-expiry'}
            loading={actions.grantWebsite.isPending || actions.updateWebsiteExpiry.isPending}
            onClose={close}
            onConfirm={(iso) =>
              dialog?.kind === 'edit-expiry' && person.website.invite_id
                ? actions.updateWebsiteExpiry.mutate(
                    { inviteId: person.website.invite_id, expires_at: iso },
                    { onSuccess: () => { close(); notification({ type: 'success', title: 'Website expiry updated' }) }, onError: fail },
                  )
                : actions.grantWebsite.mutate({ key: person.key, name: person.name ?? undefined, expires_at: iso }, { onSuccess: showLinks(person), onError: fail })
            }
          />
          <NoteDialog
            name={name}
            initial={person.note ?? ''}
            open={dialog?.kind === 'note'}
            loading={actions.setNote.isPending}
            onClose={close}
            onSave={(note) => actions.setNote.mutate({ key: person.key, note }, { onSuccess: close, onError: fail })}
          />
          <PersonTrackedLinksModal
            open={dialog?.kind === 'tracked-links'}
            person={person}
            links={linksByPerson.get(person.key)?.links ?? []}
            onClose={close}
            onCreate={() => {
              close()
              setLinkFor(person)
            }}
          />
          <SourceChannelModal
            subject="person"
            name={name}
            initial={channels?.people[person.key] ?? null}
            open={dialog?.kind === 'source-channel'}
            loading={setChannel.isPending}
            onClose={close}
            onSave={(sourceChannel) =>
              setChannel.mutate(
                { key: person.key, sourceChannel },
                { onSuccess: () => { close(); notification({ type: 'success', title: 'Source channel saved' }) }, onError: fail },
              )
            }
          />
          <ConfirmSettingModal
            open={dialog?.kind === 'regenerate-website'}
            onClose={close}
            onConfirm={() =>
              person.website.invite_id &&
              actions.regenerateWebsite.mutate({ inviteId: person.website.invite_id }, { onSuccess: showLinks(person), onError: fail })
            }
            loading={actions.regenerateWebsite.isPending}
            title="Regenerate website link?"
            confirmLabel="Regenerate"
            consequence={<>The current link for <strong>{name}</strong> stops working now. You&apos;ll get a new link to send them, shown once.</>}
          />
          <ConfirmSettingModal
            open={dialog?.kind?.startsWith('revoke-') ?? false}
            onClose={close}
            destructive
            onConfirm={() => {
              const done = { onSuccess: () => { close(); notification({ type: 'success', title: 'Access revoked' }) }, onError: fail }
              if (dialog?.kind === 'revoke-website') actions.revokeWebsite.mutate({ key: person.key }, done)
              if (dialog?.kind === 'revoke-pricing') actions.revokePricing.mutate({ key: person.key }, done)
              if (dialog?.kind === 'revoke-platform') actions.revokePlatform.mutate({ key: person.key }, done)
            }}
            loading={actions.revokeWebsite.isPending || actions.revokePricing.isPending || actions.revokePlatform.isPending}
            title={
              dialog?.kind === 'revoke-website'
                ? 'Revoke website access?'
                : dialog?.kind === 'revoke-pricing'
                  ? 'Revoke pricing access?'
                  : 'Revoke platform access?'
            }
            confirmLabel="Revoke"
            consequence={
              dialog?.kind === 'revoke-website' ? (
                <>Every website link for <strong>{name}</strong> stops working now. A browser already let in keeps access until its next daily check, at most 24 hours.</>
              ) : dialog?.kind === 'revoke-pricing' ? (
                <>Unused pricing links for <strong>{name}</strong> stop working now. A pricing session they already opened is not ended.</>
              ) : (
                <><strong>{name}</strong> is removed from the platform allowlist and can no longer create an account.</>
              )
            }
          />
        </>
      )}
    </>
  )
}
